import { useState, useEffect, useRef, useCallback } from 'react'
import { invariant } from '@vp/digital-invariant-lib-ubik'
import { useStorageRef, useStorageState } from '@vp/digital-storage-lib-ubik'
import { useIdentity, useLogger } from '@vp/ubik-context'
import { VPAuthIdentity } from '../types/Types'

type InitiateActionFn<P> = (actionParameters: P) => void
type ActionFn<P, R> = (actionParameters: P, identity: VPAuthIdentity) => R
type ActionResult<R> = R | null
type ActionError = Error | null

// P = type of action actionParameters
// R = action result
export function useSignedInAction<P, R>(action: ActionFn<P, R>, id: string): [ActionResult<R>, ActionError, InitiateActionFn<P>, boolean] {
  invariant(action, 'You must provide an action function to be executed.')
  invariant(id, 'You must assign a unique action id.')
  const logger = useLogger()
  const count = useRef(0)
  const { auth } = useIdentity()
  const theAction = useRef<ActionFn<P, R>>(action)

  const [result, setResult] = useState<ActionResult<R>>(null)
  const [error, setError] = useState<ActionError>(null)
  const [parameters, setParameters] = useStorageState<P | null>(`${id}-parameters`)
  const [signInInProgress, setSignInInProgress] = useStorageRef<boolean>(`${id}-sign-in-in-progress`, false)

  count.current += 1
  logger.debug('useSignedInAction: Hook Rendered', {
    id,
    parameters,
    count,
    result,
    error,
    signInInProgress,
    isSignedIn: auth ? auth.isSignedIn() : 'initializing',
  })

  const initiateAction = useCallback(
    (p: P): void => {
      logger.debug('useSignedInAction: Initiating action', {
        id,
        parameters: p,
      })
      setResult(null)
      setError(null)
      setParameters(p)
    },

    [logger, id, setParameters],
  )

  useEffect(() => {
    logger.debug('useSignedInAction: Executing useEffect', { id })

    const doAction = async (): Promise<void> => {
      try {
        const identity = auth?.getIdentity()
        const r: R = await theAction.current(parameters, identity)
        setResult(r)
      } catch (e) {
        setError(e)
      }
    }

    const clearState = () => {
      logger.debug('useSignedInAction: Clearing state', { id })
      setParameters(null) // clear them out
      setSignInInProgress(false)
    }

    if (result !== null || error !== null) {
      // we are done
      logger.debug('useSignedInAction: Action Complete', {
        id,
        parameters,
      })
      if (parameters) {
        clearState()
      }
    } else if (parameters) {
      // we still need to do something
      if (auth) {
        if (auth.isSignedIn()) {
          logger.debug('useSignedInAction: Doing action', {
            id,
            parameters,
          })
          doAction()
        } else if (signInInProgress) {
          // use cancelled sign-in
          logger.debug('useSignedInAction: User did not sign-in', {
            id,
            parameters,
          })
          clearState()
        } else {
          logger.debug('useSignedInAction: Signing in', {
            id,
            parameters,
          })
          setSignInInProgress(true)
          auth.signIn()
        }
      } else {
        logger.debug('useSignedInAction: Waiting for Auth to initialize', {
          id,
          parameters,
        })
      }
    }
  }, [id, auth, parameters, setParameters, error, setError, result, setResult, signInInProgress, setSignInInProgress, logger])

  return [result, error, initiateAction, !!parameters]
}
